import React from 'react';
import { cn } from 'lib/utils';

/**
 * Classes for a `RadioGroupItem` inside an exam option row: an 18px ink radio
 * that fills to a 5px ring when chosen (the shared radio's inner dot is hidden).
 */
export const EXAM_RADIO_CLASS =
    'h-[18px] w-[18px] border-[1.5px] border-foreground/20 data-[state=checked]:border-[5px] data-[state=checked]:border-foreground [&_span]:hidden';

/** Classes for a `Checkbox` inside an exam option row (multiple response). */
export const EXAM_CHECKBOX_CLASS = 'h-[18px] w-[18px]';

/** A, B, C … for the first 26 options, then plain numbers. */
export function optionLetter(index: number): string {
    return index < 26 ? String.fromCharCode(65 + index) : String(index + 1);
}

export interface ExamOptionRowProps {
    /** Id of the control inside, so the whole row selects it. */
    htmlFor: string;
    letter: string;
    selected: boolean;
    /** The radio or checkbox. */
    control: React.ReactNode;
    children: React.ReactNode;
    /** Struck out by the answer eliminator: dimmed with the text crossed through. */
    eliminated?: boolean;
    /** Shows the strike button beside the row (eliminator mode is on). */
    onToggleEliminated?: () => void;
    /** Accessible name of the strike button. */
    eliminateLabel?: string;
}

/** One answer choice: control, letter, text. 56px rows (54px on phones); the chosen row gets an ink border. */
export function ExamOptionRow({
    htmlFor,
    letter,
    selected,
    control,
    children,
    eliminated = false,
    onToggleEliminated,
    eliminateLabel,
}: ExamOptionRowProps) {
    const row = (
        <label
            htmlFor={htmlFor}
            className={cn(
                'flex min-h-[54px] cursor-pointer items-center gap-3.5 rounded-md border px-4 transition-[border-color,background-color,box-shadow,opacity] duration-150 lg:min-h-14',
                onToggleEliminated && 'min-w-0 flex-1',
                selected
                    ? 'border-foreground bg-background shadow-[0_0_0_3px_rgba(17,24,39,0.08)]'
                    : 'border-border bg-card hover:border-foreground/30',
                eliminated && 'opacity-50',
            )}
        >
            {control}
            <span className="min-w-3.5 shrink-0 text-[13px] font-semibold text-muted-foreground">{letter}</span>
            <span
                className={cn(
                    'min-w-0 flex-1 select-none py-3 text-[15px] leading-normal text-foreground',
                    selected && 'font-semibold',
                    eliminated && 'line-through decoration-2',
                )}
            >
                {children}
            </span>
        </label>
    );
    if (!onToggleEliminated) return row;
    return (
        <div className="flex items-center gap-2">
            {row}
            <button
                type="button"
                onClick={onToggleEliminated}
                aria-pressed={eliminated}
                aria-label={eliminateLabel}
                className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    eliminated
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-card text-muted-foreground line-through hover:border-foreground/30 hover:text-foreground',
                )}
            >
                {letter}
            </button>
        </div>
    );
}

export default ExamOptionRow;
